import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getToken, redirectToLogin } from '../utils/auth';
import { getImpersonationTarget } from '../hooks/useImpersonation';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add impersonation header if active
  const impersonationTarget = getImpersonationTarget();
  if (impersonationTarget) {
    config.headers['X-Impersonate-User'] = impersonationTarget.id;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

/**
 * Extract the HTTP status code from a thrown request error, or undefined for
 * transport/network errors that never produced a response. Uses axios's own
 * type guard rather than a hand-rolled structural cast.
 */
export function getHttpStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

export default apiClient;
